<template lang="pug">
loader(:loading='loading', :size='40')
  // Feature enabled/disabled
  md-layout
    md-switch.md-primary(v-model='ads.dfpEnabled') Use DCM or DFP

  md-layout(v-if='ads.dfpEnabled')
    // Network selector
    md-input-container
      label(for='network') Network
      md-select#network(v-model='ads.networkCode', :disabled='networks.length < 2')
        md-option(v-for='network in networks', :value='network.networkCode', @selected='onNetworkSelected' :key="network.networkCode") {{ network.displayName }}
    // Advertisers selector
    md-layout(md-flex='100')
      md-layout
        md-input-container
          label(for='advertiser') Advertiser
          md-select#advertiser(v-model='ads.advertiserId', :disabled='advertisers.length == 0')
            md-option(v-for='advertiser in advertisers', :value='advertiser.id', @selected='onAdvertiserSelected', :key='advertiser.id') {{ advertiser.name }}
      md-layout.select-loader(md-flex='10', md-align='end', v-if='loadingAdvertisers')
        md-spinner.md-accent(md-indeterminate='', :md-size='25')
    // Orders selector
    md-layout(md-flex='100')
      md-layout
        md-input-container
          label(for='order') Order
          md-select#order(v-model='ads.orderId', :disabled='orders.length == 0')
            md-option(v-for='order in orders', :value='order.id', @selected='onOrderSelected', :key='order.id') {{ order.name }}
      md-layout.select-loader(md-flex='10', md-align='end', v-if='loadingOrders')
        md-spinner.md-accent(md-indeterminate='', :md-size='25')
    // Line items selector
    md-layout(md-flex='100')
      md-layout
        md-input-container
          label(for='lineItem') Line Item
          md-select#order(v-model='ads.lineItemId', :disabled='lineItems.length == 0')
            md-option(v-for='lineItem in lineItems', :value='lineItem.id', @selected='onLineItemSelected', :key='lineItem.id') {{ lineItem.name }}
      md-layout.select-loader(md-flex='10', md-align='end', v-if='loadingLineItems')
        md-spinner.md-accent(md-indeterminate='', :md-size='25')
    // Ad unit selector
    md-layout(md-flex='100')
      md-layout
        md-input-container
          label(for='adUnit') Ad Unit
          md-select#adUnit(v-model='ads.adUnitTag', :disabled='adUnits.length == 0', @selected='setAdUnitId')
            md-option(v-for='adUnit in adUnits', :value='adUnit.tag', :key='adUnit.tag') {{ adUnit.name }}
      md-layout.select-loader(md-flex='10', md-align='end', v-if='loadingAdUnits')
        md-spinner.md-accent(md-indeterminate='', :md-size='25')
</template>

<script>
  import dfpService from '../services/dfpService';
  import Loader from './Loader';

  export default {
    props: {
      ads: {
        type: Object,
        required: true
      },
      platforms: {
        type: Object,
        required: true
      },
      validations: {
        type: Object,
        required: true
      },
      formSubmitted: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        networks: [],
        advertisers: [],
        orders: [],
        lineItems: [],
        adUnits: [],
        loading: false,
        loadingAdvertisers: false,
        loadingOrders: false,
        loadingLineItems: false,
        loadingAdUnits: false
      };
    },
    components: {
      Loader
    },
    computed: {
    },
    methods: {
      getNetworks() {
        this.loading = true;
        dfpService.getNetworks()
          .then((response) => {
            this.networks = response.data.result ? response.data.result.rval : [];
            this.networks.sort((a, b) => a.name.localeCompare(b.name));
            if (this.networks.length === 1) {
              this.ads.networkCode = this.networks[0].networkCode;
            }
            this.loading = false;
          })
          .catch(() => {
            this.$emit('error', 'Error while retrieving the list of networks');
            this.loading = false;
          });
      },
      onNetworkSelected() {
        // Reset form controls because the network has changed
        this.advertisers = [];
        this.orders = [];
        this.lineItems = [];
        this.adUnits = [];
        this.ads.advertiserId = null;
        this.ads.orderId = null;
        this.ads.lineItemId = null;
        this.ads.adUnitTag = null;
        this.ads.adUnitId = null;
      },
      onAdvertiserSelected() {
        // Reset form controls because the advertiser has changed
        this.orders = [];
        this.lineItems = [];
        this.adUnits = [];
        this.ads.orderId = null;
        this.ads.lineItemId = null;
        this.ads.adUnitTag = null;
        this.ads.adUnitId = null;
      },
      onOrderSelected() {
        // Reset form controls because the order has changed
        this.lineItems = [];
        this.adUnits = [];
        this.ads.lineItemId = null;
        this.ads.adUnitTag = null;
        this.ads.adUnitId = null;
      },
      onLineItemSelected() {
        // Reset form controls because the line item has changed
        this.adUnits = [];
        this.ads.adUnitTag = null;
        this.ads.adUnitId = null;
      },
      resetForm() {
        this.networks = [];
        this.advertisers = [];
        this.orders = [];
        this.lineItems = [];
        this.adUnits = [];
        this.ads.networkCode = null;
        this.ads.advertiserId = null;
        this.ads.orderId = null;
        this.ads.lineItemId = null;
        this.ads.adUnitTag = null;
        this.ads.adUnitId = null;
      },
      setAdUnitId(adUnitTag) {
        this.ads.adUnitId = adUnitTag ? adUnitTag.split('/').reverse()[0] : null;
      }
    },
    mounted() {
      if (this.ads.dfpEnabled) {
        this.getNetworks();
      }
    },
    watch: {
      'ads.dfpEnabled': function (newVal, oldVal) {
        if (newVal !== oldVal) {
          if (newVal) {
            this.$store.dispatch('user/enableDfp')
              .then(() => {
                this.ads.default = '';
                this.getNetworks();
              })
              .catch(() => {
                // Revert operation
                this.ads.dfpEnabled = false;
              });
          } else {
            this.resetForm();
          }
        }
      },
      'ads.networkCode': function (networkCode) {
        if (networkCode) {
          this.loadingAdvertisers = true;
          dfpService.getAdvertisers(networkCode)
            .then((response) => {
              const hasResults = response.data.result.rval.totalResultSetSize > 0;
              this.advertisers = hasResults ? response.data.result.rval.results : [];
              this.advertisers.sort((a, b) => a.name.localeCompare(b.name));
              if (this.advertisers.length === 1) {
                this.ads.advertiserId = this.advertisers[0].id;
              }
              this.loadingAdvertisers = false;
            })
            .catch(() => {
              this.$emit('error', `Error while retrieving the advertisers for network ${networkCode}`);
              this.loadingAdvertisers = false;
            });
        }
      },
      'ads.advertiserId': function (advertiserId) {
        if (advertiserId) {
          this.loadingOrders = true;
          dfpService.getAdvertiserOrders(this.ads.networkCode, advertiserId)
            .then((response) => {
              const hasResults = response.data.result.rval.totalResultSetSize > 0;
              this.orders = hasResults ? response.data.result.rval.results : [];
              this.orders.sort((a, b) => a.name.localeCompare(b.name));
              if (this.orders.length === 1) {
                this.ads.orderId = this.orders[0].id;
              }
              this.loadingOrders = false;
            })
            .catch(() => {
              this.$emit('error', `Error while retrieving the orders for advertiser ${advertiserId}`);
              this.loadingOrders = false;
            });
        }
      },
      'ads.orderId': function (orderId) {
        if (orderId) {
          this.loadingLineItems = true;
          dfpService.getOrderLineItems(this.ads.networkCode, orderId)
            .then((response) => {
              const hasResults = response.data.result.rval.totalResultSetSize > 0;
              this.lineItems = hasResults ? response.data.result.rval.results : [];
              this.lineItems.sort((a, b) => a.name.localeCompare(b.name));
              if (this.lineItems.length === 1) {
                this.ads.lineItemId = this.lineItems[0].id;
              }
              this.loadingLineItems = false;
            })
            .catch(() => {
              this.$emit('error', `Error while retrieving the line items for order ${orderId}`);
              this.loadingLineItems = false;
            });
        }
      },
      'ads.lineItemId': function (lineItemId) {
        if (lineItemId) {
          this.loadingAdUnits = true;
          const networkCode = this.ads.networkCode;
          dfpService.getLineItemAdUnits(networkCode, lineItemId)
            .then((response) => {
              const hasResults = response.data.result.rval.totalResultSetSize > 0;
              this.adUnits = hasResults ? response.data.result.rval.results : [];
              this.adUnits.forEach((adUnit) => {
                Object.assign(adUnit, { tag: dfpService.buildVastTag(networkCode, adUnit) });
              });
              this.adUnits.sort((a, b) => a.name.localeCompare(b.name));
              if (this.adUnits.length === 1) {
                this.ads.adUnitTag = this.adUnits[0].tag;
              }
              this.loadingAdUnits = false;
            })
            .catch(() => {
              this.$emit('error', `Error while retrieving the ad units for line item ${lineItemId}`);
              this.loadingAdUnits = false;
            });
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .subtitle {
    line-height: 32px;
    color: rgba(0, 0, 0, 0.54);
  }
  .select-loader {
    margin-top: 25px;
  }
  .validation-error-message {
    width: 100%;
    margin: 10px 0;
    padding: 15px;
    background-color: #ff5722;
    color: #EBFAFF;
    font-size: 1.1em;
    .md-icon {
      margin-top: -2px;
      margin-right: 5px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/shared-components/Dfp.vue